import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import "../../page.css";
import "../../home.css";
import "../../trekking/kilimanjaro.css";
import "./serengeti.css";
import "./northern-circuits.css";

const features = [
  {
    title: "Four parks, one loop",
    text: "Tarangire, Lake Manyara, Ngorongoro, and the Serengeti sit on a practical route from Arusha. You can run a short highlight safari or stretch the nights for deeper time on the plains.",
  },
  {
    title: "Different habitats each day",
    text: "Baobab woodland, groundwater forest, a volcanic crater floor, and endless grassland. The circuit stays interesting because the landscape keeps changing.",
  },
  {
    title: "Migration and resident game",
    text: "Serengeti timing shapes many itineraries, but Tarangire dry-season elephants, crater density, and Manyara birds keep the loop strong through much of the year.",
  },
  {
    title: "Easy Zanzibar finish",
    text: "Fly out from Arusha or Kilimanjaro after the bush. Many guests add beach nights once the safari days are done.",
  },
];

const circuitStats = [
  { value: "4", label: "national parks" },
  { value: "5–10", label: "typical safari days" },
  { value: "Arusha", label: "usual start point" },
  { value: "Year round", label: "strong game options" },
];

const parks = [
  {
    id: "serengeti",
    name: "Serengeti National Park",
    stay: "2 to 4 nights",
    mood: "Endless plains",
    bestFor: "Migration, predators, and big-sky game drives",
    lodging: "Lodge or tented camp",
    to: "/safaris/northern-circuits/serengeti",
    image:
      "https://images.unsplash.com/photo-1516426122078-c23e76319801?auto=format&fit=crop&w=1200&q=80",
    imageAlt: "Wildlife on open Serengeti savanna plains",
    summary:
      "Tanzania’s signature safari park. Open grassland, rocky kopjes, and river lines hold lions, leopards, cheetahs, and huge herds across a landscape that still feels endless.",
    body: "The Serengeti is where many northern circuits spend the most nights. Central Seronera works year round for resident game. Southern Ndutu shines in the green-season calving months. The western corridor and far north come into play when the great migration moves. We match camp areas to your travel month so you watch wildlife instead of crossing empty ground.",
    highlights: [
      "UNESCO World Heritage wilderness of nearly 15,000 km²",
      "Great migration of wildebeest and zebra with the rains",
      "Strong predator country across open plains and riverine bush",
      "Balloon safaris and classic morning and afternoon drives",
    ],
    notes:
      "Plan two or more nights here whenever you can. One night only skims the surface of a park this large.",
  },
  {
    id: "ngorongoro",
    name: "Ngorongoro Crater",
    stay: "1 to 2 nights",
    mood: "Volcanic caldera",
    bestFor: "Dense Big Five viewing in a single dramatic arena",
    lodging: "Rim lodge or highland camp",
    to: "/safaris/northern-circuits/ngorongoro",
    image:
      "https://images.unsplash.com/photo-1547471080-7cc2caa01a7e?auto=format&fit=crop&w=1200&q=80",
    imageAlt: "Open highland grassland under a wide East African sky",
    summary:
      "A collapsed volcano holding grassland, forest, and lakes on the crater floor. Lions, elephants, buffalo, hippos, and rare black rhinos share a compact world below steep walls.",
    body: "Ngorongoro sits between Arusha and the Serengeti, so most private loops spend a night on the cool highland rim. You descend after breakfast, spend the day on the floor among herds and wetlands, then climb out before dark. Sightings often come quickly because the wildlife is concentrated. Rim lodges add misty mornings and wide views into the caldera.",
    highlights: [
      "UNESCO crater floor with exceptional game density",
      "Chance to see all of the Big Five, including black rhino",
      "Highland lodging above the clouds on many mornings",
      "Natural bridge between Manyara and the Serengeti",
    ],
    notes:
      "One full crater day is the classic plan. A second night helps if you want a slower rim evening or a second descent.",
  },
  {
    id: "tarangire",
    name: "Tarangire National Park",
    stay: "1 to 3 nights",
    mood: "Elephants and baobabs",
    bestFor: "Dry-season herds and a quieter start or finish",
    lodging: "Lodge or tented camp",
    to: "/safaris/northern-circuits/tarangire",
    image:
      "https://images.unsplash.com/photo-1516426122078-c23e76319801?auto=format&fit=crop&w=1200&q=80",
    imageAlt: "Wildlife and open savanna under wide African sky",
    summary:
      "Baobab woodland and river country famous for large elephant herds. When the bush dries out, wildlife gathers along the Tarangire River and Silale Swamp.",
    body: "Tarangire feels different from Serengeti plains: sculptural baobabs, open woodland, and a river that becomes the park’s lifeline in the dry months. It is often the first or last park on a northern loop, with fewer vehicles than the crater or central Serengeti on many weeks. Birdlife is rich, and predators follow the same water lines as the herds.",
    highlights: [
      "Large elephant gatherings in the dry season",
      "Ancient baobab landscapes that photograph beautifully",
      "Strong birding along river and swamp edges",
      "Calm pacing as an opener or closer to the circuit",
    ],
    notes:
      "Dry months from roughly June to October are peak for game density. Greener months still work, with animals more spread out.",
  },
  {
    id: "lake-manyara",
    name: "Lake Manyara National Park",
    stay: "Half day to 1 night",
    mood: "Forest and lake edge",
    bestFor: "Birds, shade, and a compact Rift Valley stop",
    lodging: "Lodge or camp nearby",
    to: "/safaris/northern-circuits/lake-manyara",
    image:
      "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?auto=format&fit=crop&w=1200&q=80",
    imageAlt: "Lake and escarpment landscape in soft light",
    summary:
      "Groundwater forest against the Rift escarpment, with a shallow alkaline lake beyond. A smaller park known for elephants in the canopy shade, rich birdlife, and tree-climbing lions.",
    body: "Manyara is the intimate contrast day on the northern circuit. Tall trees cool the morning, hippos and waterbirds work the shoreline, and flamingos can pink the shallows when water levels suit them. It fits cleanly between Tarangire and Ngorongoro as a focused full day or a single overnight, without needing the multi-night commitment of the Serengeti.",
    highlights: [
      "Groundwater forest against dramatic escarpment cliffs",
      "Lake-edge hippos, storks, and seasonal flamingo flocks",
      "Famous for lions that sometimes rest in trees",
      "Ideal half-day or one-night link on the classic loop",
    ],
    notes:
      "Treat Manyara as a character stop, not a long base. It adds habitat variety without slowing a Serengeti-focused safari.",
  },
];

const loopSteps = [
  {
    title: "Start near Arusha",
    text: "Most private safaris leave from Arusha or Kilimanjaro Airport. We set vehicles, guides, and the first park order around your arrival time.",
  },
  {
    title: "Work west through the parks",
    text: "A common flow is Tarangire, then Manyara, then Ngorongoro rim, then into the Serengeti. We reverse or shorten the sequence when flights or season demand it.",
  },
  {
    title: "Finish on the plains or the beach",
    text: "End with Serengeti nights, return to Arusha, or fly on to Zanzibar for a soft landing after the bush.",
  },
];

const practical = [
  {
    title: "How many days",
    text: "Five to seven days covers highlights. Eight to ten nights lets you add a second Serengeti area or a slower Tarangire stay.",
  },
  {
    title: "Best months",
    text: "Dry months favour Tarangire and easy roads. December to March suits southern Serengeti calving. Mid-year suits western and northern migration drama.",
  },
  {
    title: "Private pacing",
    text: "You travel with your own guide and vehicle. We keep daily distances honest so game drives stay the point of the day.",
  },
];

function NorthernCircuits() {
  const location = useLocation();
  const statsRef = useRef(null);
  const [statsVisible, setStatsVisible] = useState(false);

  useEffect(() => {
    document.title = "Northern Circuit · Mirrorkhan Tours";
  }, []);

  useEffect(() => {
    if (!location.hash) {
      window.scrollTo(0, 0);
      return;
    }
    const id = location.hash.replace("#", "");
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [location]);

  useEffect(() => {
    const node = statsRef.current;
    if (!node) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setStatsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.25 }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="kili-page northern-circuits-page">
      <section className="kili-hero serengeti-hero northern-circuits-hero">
        <div className="kili-hero__media" aria-hidden="true">
          <img
            src="https://images.unsplash.com/photo-1516426122078-c23e76319801?auto=format&fit=crop&w=1800&q=80"
            alt=""
          />
        </div>
        <div className="kili-hero__shade" aria-hidden="true" />
        <div className="container kili-hero__content">
          <div className="row">
            <div className="col-lg-8 col-xl-7 mk-hero-enter">
              <p className="eyebrow eyebrow--light mb-3">
                <Link to="/safaris" className="text-decoration-none">
                  Safaris
                </Link>
              </p>
              <h1 className="mb-3">Northern Circuit</h1>
              <p className="kili-hero__lede mb-4">
                The classic Tanzania safari loop. Private days across Serengeti
                plains, Ngorongoro crater, Tarangire elephants, and Lake
                Manyara forest, shaped around your dates.
              </p>
              <div className="d-flex flex-wrap gap-3">
                <a className="btn btn-mk-gold" href="#parks">
                  Explore the parks
                </a>
                <Link className="btn btn-mk-ghost" to="/contact">
                  Plan this circuit
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section
        ref={statsRef}
        className={
          statsVisible ? "serengeti-stats is-visible" : "serengeti-stats"
        }
        aria-label="Northern Circuit facts"
      >
        <div className="container">
          <ul className="serengeti-stats__list list-unstyled mb-0">
            {circuitStats.map((stat, index) => (
              <li
                className="serengeti-stats__item"
                key={stat.label}
                style={{ "--stat-delay": `${index * 120}ms` }}
              >
                <span className="serengeti-stats__value">{stat.value}</span>
                <span className="serengeti-stats__label">{stat.label}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="section-block">
        <div className="container">
          <div className="row mb-4 mb-lg-5">
            <div className="col-lg-8">
              <p className="eyebrow">The circuit</p>
              <h2 className="mb-3">Why northern Tanzania still sets the standard</h2>
              <p className="page-lead mb-0">
                It is the most travelled safari region in the country for a
                reason: reliable wildlife, short hops between parks, and enough
                habitat variety to fill a week without feeling repetitive.
              </p>
            </div>
          </div>

          <div className="row g-4">
            {features.map((item) => (
              <div className="col-md-6" key={item.title}>
                <article className="kili-feature h-100">
                  <h3 className="h4 mb-2">{item.title}</h3>
                  <p className="mb-0">{item.text}</p>
                </article>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="parks" className="section-block home-section--soft">
        <div className="container">
          <div className="row mb-4 mb-lg-5">
            <div className="col-lg-8">
              <p className="eyebrow">National parks</p>
              <h2 className="mb-3">Four parks that build the northern loop</h2>
              <p className="page-lead mb-0">
                Each stop has a clear role. Read the summaries below, then open
                a park page for seasons, experiences, and practical detail.
              </p>
            </div>
          </div>

          <div className="kili-routes">
            {parks.map((park, index) => (
              <article
                className={
                  index % 2 === 1
                    ? "kili-route kili-route--flip"
                    : "kili-route"
                }
                key={park.id}
                id={park.id}
              >
                <div className="kili-route__top">
                  <div className="kili-route__media">
                    <img src={park.image} alt={park.imageAlt} />
                  </div>
                  <div className="kili-route__intro">
                    <p className="kili-route__meta mb-2">
                      {park.stay}
                      <span aria-hidden="true"> · </span>
                      {park.mood}
                    </p>
                    <h3 className="h3 mb-3">{park.name}</h3>
                    <p className="kili-route__summary">{park.summary}</p>
                    <p className="kili-route__body-copy mb-0">{park.body}</p>
                  </div>
                </div>

                <div className="kili-route__panel">
                  <dl className="kili-route__facts">
                    <div>
                      <dt>Best for</dt>
                      <dd>{park.bestFor}</dd>
                    </div>
                    <div>
                      <dt>Typical stay</dt>
                      <dd>{park.stay}</dd>
                    </div>
                    <div>
                      <dt>Lodging</dt>
                      <dd>{park.lodging}</dd>
                    </div>
                    <div>
                      <dt>Mood</dt>
                      <dd>{park.mood}</dd>
                    </div>
                  </dl>

                  <div className="row g-4">
                    <div className="col-lg-8">
                      <h4 className="kili-route__subhead">What stands out</h4>
                      <ul className="trek-points mb-0">
                        {park.highlights.map((item) => (
                          <li key={item}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  <p className="kili-route__note mb-3">{park.notes}</p>
                  <Link className="text-link" to={park.to}>
                    Explore {park.name}
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="section-block">
        <div className="container">
          <div className="row mb-4 mb-lg-5">
            <div className="col-lg-8">
              <p className="eyebrow">How it flows</p>
              <h2 className="mb-3">A simple shape for private circuits</h2>
              <p className="page-lead mb-0">
                We rearrange parks for migration timing, lodge availability, and
                flight days. This is the outline most guests recognise.
              </p>
            </div>
          </div>

          <div className="row g-4">
            {loopSteps.map((item, index) => (
              <div className="col-md-4" key={item.title}>
                <article className="approach-item h-100">
                  <p className="northern-circuits-step mb-2">
                    Step {index + 1}
                  </p>
                  <h3 className="h4 mb-2">{item.title}</h3>
                  <p className="mb-0">{item.text}</p>
                </article>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-block home-section--soft">
        <div className="container">
          <div className="row mb-4 mb-lg-5">
            <div className="col-lg-8">
              <p className="eyebrow">Before you go</p>
              <h2 className="mb-0">Practical notes from the desk</h2>
            </div>
          </div>

          <div className="row g-4">
            {practical.map((item) => (
              <div className="col-md-4" key={item.title}>
                <article className="approach-item h-100">
                  <h3 className="h4 mb-2">{item.title}</h3>
                  <p className="mb-0">{item.text}</p>
                </article>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="home-close">
        <div className="container">
          <div className="row align-items-center g-4">
            <div className="col-lg-8">
              <p className="eyebrow eyebrow--light">Plan your safari</p>
              <h2 className="mb-3">
                Tell us your month, nights, and which parks you want most.
              </h2>
              <p className="mb-0 home-close__copy">
                We reply with a clear circuit outline, camp options, and a price
                for your group size, with or without a Zanzibar finish.
              </p>
            </div>
            <div className="col-lg-4 text-lg-end">
              <Link className="btn btn-mk-gold" to="/contact">
                Contact the desk
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default NorthernCircuits;
